import { toolPreparingLabel } from "@/lib/tool-catalog";

export interface ThinkingStep {
	id: string;
	/** Short label for the step (usually first line of the thought). */
	label: string;
	/** Optional timestamp in ms since epoch. */
	timestamp?: number;
}

interface ThinkingBlockProps {
	/** When true, render a live "考察中" indicator even if steps is empty. */
	live?: boolean;
	/** compaction の要約生成中は文言を差し替える。 */
	compacting?: boolean;
	/** advisor の server-side review 中は文言を差し替える。 */
	advising?: boolean;
	preparingTool?: boolean;
	toolName?: string;
	steps?: readonly ThinkingStep[];
	/** Whether the list should be initially expanded. Defaults to false. */
	defaultOpen?: boolean;
}

function formatMs(ts?: number): string {
	if (!ts) return "";
	const d = new Date(ts);
	const hh = `${d.getHours()}`.padStart(2, "0");
	const mm = `${d.getMinutes()}`.padStart(2, "0");
	const ss = `${d.getSeconds()}`.padStart(2, "0");
	return `${hh}:${mm}:${ss}`;
}

const containerStyle: React.CSSProperties = {
	margin: "4px 0 8px",
	fontFamily: "var(--font-mono)",
	fontSize: "var(--text-caption)",
	color: "var(--fg-2)",
};

function BouncingSquare() {
	return (
		<span
			style={{
				display: "inline-flex",
				alignItems: "flex-end",
				justifyContent: "center",
				width: 6,
				height: 12,
			}}
		>
			<span
				className="izanagi-bouncing-square"
				style={{
					width: 6,
					height: 6,
					borderRadius: "var(--radius-1)",
					background: "var(--fg-1)",
					transformOrigin: "bottom",
					animation: "izanagi-bounce-square 0.6s ease-in-out infinite",
				}}
			/>
		</span>
	);
}

export function ThinkingBlock({
	live = false,
	compacting = false,
	advising = false,
	preparingTool = false,
	toolName,
	steps = [],
	defaultOpen = false,
}: ThinkingBlockProps) {
	const count = steps.length;

	if (live && count === 0) {
		return (
			<div
				data-testid="thinking-block"
				data-state={
					preparingTool
						? "preparing-tool"
						: advising
							? "advising"
							: compacting
								? "compacting"
								: "live"
				}
				style={containerStyle}
			>
				<span
					style={{
						display: "inline-flex",
						alignItems: "center",
						gap: 6,
					}}
				>
					<BouncingSquare />
					{preparingTool
						? toolPreparingLabel(toolName)
						: advising
							? "レビューを依頼しています"
							: compacting
								? "コンテキストを圧縮しています"
								: "考察中"}
				</span>
			</div>
		);
	}

	if (count === 0) return null;

	return (
		<details
			data-testid="thinking-block"
			data-state={live ? "live-with-steps" : "idle"}
			open={defaultOpen}
			style={containerStyle}
		>
			<summary
				style={{
					cursor: "pointer",
					listStyle: "none",
					userSelect: "none",
					color: "var(--fg-2)",
				}}
			>
				考察 · {count}ステップ
			</summary>
			<ul
				className="select-text"
				style={{
					listStyle: "none",
					margin: "6px 0 0",
					padding: 0,
					display: "grid",
					gridTemplateColumns: "40px 1fr",
					rowGap: 4,
				}}
			>
				{steps.map((step) => (
					<li
						key={step.id}
						style={{
							display: "contents",
							color: "var(--fg-2)",
						}}
					>
						<span style={{ color: "var(--fg-3)" }}>{formatMs(step.timestamp)}</span>
						<span style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{step.label}</span>
					</li>
				))}
			</ul>
		</details>
	);
}
